import { defineStore } from 'pinia';
import { watch } from 'vue';
import { client, getErrorMessage } from '@/api';
import { ElMessage } from 'element-plus';
import {
  CommentComplaintDto,
  CommentComplaintStatusDto,
  CommentsSortByDto,
  PaginatedCommentComplaintsResponseDto,
  SortDirectionDto,
  UpdateCommentComplaintRequestDto,
} from '@/api/models';
import { ListGameCommentComplaintsParameters } from '@/api/parameters';
import { useUserStore } from '@/store/user';
import { capitalize, debounce } from 'es-toolkit';
import invariant from 'tiny-invariant';
import { UserProfile } from '@/api/models/user-profile.dto';

export const COMPLAINT_COLOR_STATUS: Record<string, string> = {
  new: '#b2b2b2',
  reviewed: '#67c23a',
};

export type ComplaintsState = {
  isWatching: boolean;
  loading: boolean;
  error: string | null;
  onlyNew: boolean;
  search: string | null;

  sortName: CommentsSortByDto | null;
  sortDirection: SortDirectionDto | null;

  complaints: CommentComplaintDto[];
  complaint: (CommentComplaintDto & UserProfile) | null;

  pagination: {
    page: number;
    perPage: number;
    total: number;
    pageCount: number;
  };
};

const DEFAULT_PAGINATION = {
  page: 1,
  perPage: 15,
  total: 0,
  pageCount: 0,
};

const getInitState = () => ({
  routeState: null,

  isWatching: false,
  loading: false,
  error: null,
  onlyNew: false,
  search: null,
  status: [],

  sortName: 'createdAt' as CommentsSortByDto,
  sortDirection: 'DESC' as SortDirectionDto,

  complaints: [],
  complaint: null,

  pagination: { ...DEFAULT_PAGINATION },
});

export const useComplaints = defineStore('complaints', {
  state: (): ComplaintsState => getInitState(),

  actions: {
    init() {
      const debounceFetch = debounce(() => {
        if (this.isWatching) {
          this.refresh();
        }
      }, 300);

      watch(
        () => [this.onlyNew, this.search, this.sortName, this.sortDirection],
        () => debounceFetch(),
        { immediate: true },
      );
    },

    reset() {
      this.$reset();
    },

    async refresh() {
      this.listGameCommentComplaints({});
    },

    setLoading(value: boolean) {
      this.loading = value;
    },
    setError(value: string | null) {
      this.error = value;
    },
    setComplaint(complaint: (CommentComplaintDto & UserProfile) | null) {
      this.complaint = complaint;
    },

    getTitleStatus(status: string) {
      if (status === 'reviewed') {
        return 'Processed';
      }

      return capitalize(status);
    },

    setPagination({ page, limit }: { page: number; limit: number }): void {
      this.pagination = {
        ...this.pagination,
        page,
        perPage: limit,
      };

      this.listGameCommentComplaints({});
    },

    setSort({ prop, order }: { prop: string; order: string | null }): void {
      if (order === null) {
        this.sortDirection = null;
        this.sortName = null;
      } else {
        this.sortName = prop as CommentsSortByDto;
        this.sortDirection = order === 'ascending' ? 'ASC' : 'DESC';
      }
    },

    async toReviewed(id?: number) {
      const userStore = useUserStore();
      const complaintId = id ?? this.complaint?.id;

      invariant(complaintId, 'Complaint id is required');

      await this.updateCommentComplaint(complaintId, {
        moderator: userStore.info.email as string,
        status: 'reviewed',
      });

      await this.refresh();
    },

    getStatusColor(status: string): string {
      return COMPLAINT_COLOR_STATUS[status] ?? 'gray';
    },

    getStatus(): CommentComplaintStatusDto[] {
      return this.onlyNew ? ['new'] : ['new', 'reviewed'];
    },

    async listGameCommentComplaints(
      dto: ListGameCommentComplaintsParameters,
    ): Promise<PaginatedCommentComplaintsResponseDto> {
      this.setError(null);
      this.setLoading(true);

      let data: PaginatedCommentComplaintsResponseDto = {
        items: [],
        total: 0,
        page: 1,
        perPage: 10,
        pageCount: 1,
      };

      try {
        data = await client.complaintsApi.listGameCommentComplaints({
          ...dto,
          status: this.getStatus(),
          query:
            this.search && this.search.length >= 3 ? this.search : undefined,

          pageNumber: this.pagination.page,
          pageSize: this.pagination.perPage,

          pageSortBy: this.sortName ? this.sortName : 'createdAt',
          pageSortDirection: this.sortDirection ? this.sortDirection : 'DESC',
        } as any);

        const { items, ...pagination } = data;

        this.complaints = items ?? [];
        this.pagination = pagination;
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error during getting list of complaints',
        );

        ElMessage.error(message);

        this.setError(message);
      } finally {
        this.setLoading(false);
      }

      return data;
    },

    async getCommentComplaint(
      id: number,
    ): Promise<(CommentComplaintDto & UserProfile) | null> {
      this.setError(null);
      this.setLoading(true);

      let complaint: (CommentComplaintDto & UserProfile) | null = null;

      try {
        complaint = await client.complaintsApi.getCommentComplaint(id);

        this.setComplaint(complaint);
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error during getting comment complaint',
        );

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return complaint;
    },

    async updateCommentComplaint(
      id: number,
      dto: UpdateCommentComplaintRequestDto,
    ): Promise<(CommentComplaintDto & UserProfile) | null> {
      this.setError(null);
      this.setLoading(true);

      let complaint: (CommentComplaintDto & UserProfile) | null = null;

      try {
        complaint = await client.complaintsApi.updateCommentComplaint(id, dto);

        this.setComplaint(complaint);

        ElMessage.success('Comment complaint was updated successfully');
      } catch (err) {
        const message = getErrorMessage(
          err,
          'Error during updating comment complaint',
        );

        ElMessage.error(message);
      } finally {
        this.setLoading(false);
      }

      return complaint;
    },
  },
});
